import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import LargeLogo from './assets/images/Logo Large.png';
import './App.css';

// Layout Components
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';

// Section Components
import HeroSection from './components/ui/HeroSection';
import AboutSection from './components/ui/AboutSection';
import ProductsSection from './components/ui/ProductsSection';
import ContactSection from './components/ui/ContactSection';

// Admin Components
import AdminPanel from './pages/admin/AdminPanel';

// Landing Page Component
const LandingPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);

  // Loading animation
  useEffect(() => {
    // Simulate initial loading time for assets
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);
    
    return () => clearTimeout(timer);
  }, []);
  
  // Smooth scrolling effect
  useEffect(() => {
    document.documentElement.style.scrollBehavior = 'smooth';
    
    // Initialize parallax effects for elements with the class
    const handleScroll = () => {
      const parallaxElements = document.querySelectorAll('.parallax-bg');
      parallaxElements.forEach((element) => {
        const elementTop = element.getBoundingClientRect().top;
        const windowHeight = window.innerHeight;
        if (elementTop < windowHeight && elementTop > -windowHeight) {
          const speed = 0.15;
          const yPos = -(elementTop * speed);
          (element as HTMLElement).style.transform = `translateY(${yPos}px)`;
        }
      });
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      document.documentElement.style.scrollBehavior = 'auto';
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-background flex items-center justify-center">
        <div className="text-center">
          <img 
            src={LargeLogo}
            alt="Mad Anglers" 
            className="h-20 w-auto mx-auto mb-4 animate-pulse-slow"
          />
          <div className="inline-block w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
          <h2 className="text-2xl font-heading font-bold text-gradient">MAD ANGLERS</h2>
          <p className="text-gray-600">Loading professional fishing equipment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-background text-text font-body antialiased">
      <Header />
      
      <main className="flex-grow">
        <HeroSection />
        <AboutSection />
        <ProductsSection />
        <ContactSection />
      </main>
      
      {/* Back to top button */}
      <BackToTopButton />
      
      <Footer />
    </div>
  );
};

// Back to top button component
const BackToTopButton: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const toggleVisibility = () => {
      setIsVisible(window.pageYOffset > 500);
    };
    
    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);
  
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  
  return (
    <button 
      onClick={scrollToTop} 
      className={`fixed bottom-6 right-6 p-3 rounded-full bg-primary text-white shadow-lg transition-all duration-300 z-50 hover:bg-primary/90 hover:-translate-y-1 ${
        isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-75 pointer-events-none'
      }`}
      aria-label="Back to top"
    >
      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
      </svg>
    </button>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/admin/*" element={<AdminPanel />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
};

export default App;
